import React from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Tag, Button } from "@jem-open/jem-ui";
import { Users, Paperclip, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import McmAnnouncement from "../services/model/McmAnnouncement";
import { getDisplayStatus, downloadMediaFile } from "../services/model/McmAnnouncementUtils";
import MessageActionButtons from "../elements/MessageActionButtons";

type MessageTableProps = {
  messages: McmAnnouncement[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  totalCount: number;
  searchQuery: string;
  ordering: string;
  onSearchChange: (query: string) => void;
  onPageChange: (page: number) => void;
  onSortChange: (ordering: string) => void;
  onView: (message: McmAnnouncement) => void;
  onEdit: (message: McmAnnouncement) => void;
  onDelete: (message: McmAnnouncement) => void;
};

const statusMap: Record<string, { label: string; variant: "drafted" | "success" | "failed" | "processing" | "pending" }> = {
  draft: { label: "DRAFT", variant: "drafted" },
  scheduled: { label: "SCHEDULED", variant: "processing" },
  live: { label: "LIVE", variant: "success" },
  expired: { label: "EXPIRED", variant: "failed" },
  closed: { label: "CLOSED", variant: "failed" },
};

const SortableHeader: React.FC<{
  title: string;
  field: string;
  ordering: string;
  onSortChange: (ordering: string) => void;
}> = ({ title, field, ordering, onSortChange }) => {
  const isAsc = ordering === field;
  const isDesc = ordering === `-${field}`;

  const handleClick = (): void => {
    if (isDesc) {
      onSortChange(field);
    } else {
      onSortChange(`-${field}`);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex items-center gap-xxs text-xs font-semibold text-greyscale-text-caption uppercase hover:text-greyscale-text-title transition-colors"
    >
      {title}
      {/* eslint-disable-next-line no-nested-ternary */}
      {isDesc ? (
        <ArrowDown size={14} />
      ) : isAsc ? (
        <ArrowUp size={14} />
      ) : (
        <ArrowUpDown size={14} className="opacity-40" />
      )}
    </button>
  );
};

const HeaderLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs font-semibold text-greyscale-text-caption uppercase">{children}</span>
);

const MessageTable: React.FC<MessageTableProps> = ({
  messages,
  isLoading,
  page,
  totalPages,
  totalCount,
  searchQuery,
  ordering,
  onSearchChange,
  onPageChange,
  onSortChange,
  onView,
  onEdit,
  onDelete,
}) => (
  <>
    <div className="flex items-center mb-md">
      <div className="relative w-[300px]">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-greyscale-text-caption pointer-events-none" />
        <input
          type="text"
          aria-label="Search messages"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search messages..."
          className="h-10 w-full rounded-md border border-greyscale-border pl-9 pr-md text-sm text-greyscale-text-title outline-none focus:ring-2 focus:ring-offset-2"
        />
      </div>
    </div>

    {isLoading ? (
      <p className="text-sm text-greyscale-text-caption py-md">Loading announcements...</p>
    ) : (
      <>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead><SortableHeader title="Message" field="title" ordering={ordering} onSortChange={onSortChange} /></TableHead>
              <TableHead><HeaderLabel>Companies</HeaderLabel></TableHead>
              <TableHead><HeaderLabel>Recipients</HeaderLabel></TableHead>
              <TableHead><HeaderLabel>Sent By</HeaderLabel></TableHead>
              <TableHead><SortableHeader title="Sent Date" field="date_published" ordering={ordering} onSortChange={onSortChange} /></TableHead>
              <TableHead><SortableHeader title="Closes" field="scheduled_end_date" ordering={ordering} onSortChange={onSortChange} /></TableHead>
              <TableHead><HeaderLabel>Attachments</HeaderLabel></TableHead>
              <TableHead><SortableHeader title="Status" field="display_status" ordering={ordering} onSortChange={onSortChange} /></TableHead>
              <TableHead><span className="sr-only">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {messages.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-lg">
                  <p className="text-sm text-greyscale-text-caption">No announcements found.</p>
                </TableCell>
              </TableRow>
            )}
            {messages.map((msg) => {
              const displayStatus = getDisplayStatus(msg);
              const statusInfo = statusMap[displayStatus] || { label: displayStatus.toUpperCase(), variant: "drafted" as const };
              return (
                <TableRow key={msg.id}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-semibold text-greyscale-text-title">{msg.title}</p>
                      <p className="text-xs text-greyscale-text-caption">{msg.announcement_type?.name ?? "\u2014"}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{msg.target_employer_count}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-xxs">
                      <Users size={14} className="text-greyscale-text-caption" />
                      <span className="text-sm">{msg.announcements_read_count}/{msg.audience_snapshot_total}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {msg.created_by?.first_name && msg.created_by?.last_name
                        ? `${msg.created_by.first_name} ${msg.created_by.last_name}`
                        : "\u2014"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {msg.date_published ? new Date(msg.date_published).toLocaleString() : "\u2014"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {msg.scheduled_end_date ? new Date(msg.scheduled_end_date).toLocaleString() : "\u2014"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {msg.media_url ? (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center hover:text-greyscale-text-title transition-colors"
                        onClick={() => downloadMediaFile(msg.media_url as string)}
                        aria-label="Download attachment"
                      >
                        <Paperclip size={16} className="text-greyscale-text-caption" />
                      </button>
                    ) : (
                      <Paperclip size={16} className="text-greyscale-border" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tag variant={statusInfo.variant}>{statusInfo.label}</Tag>
                  </TableCell>
                  <TableCell>
                    <MessageActionButtons
                      message={msg}
                      onView={() => onView(msg)}
                      onEdit={() => onEdit(msg)}
                      onDelete={() => onDelete(msg)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {totalCount > 0 && (
          <div className="flex flex-col items-center gap-xs mt-md">
            {totalPages > 1 && (
              <div className="flex items-center gap-xs">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => onPageChange(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-greyscale-text-caption">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => onPageChange(page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
            <span className="text-sm text-greyscale-text-caption">
              Showing {messages.length} of {totalCount} message{totalCount !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </>
    )}
  </>
);

export default MessageTable;
